// Whether a muted learner may open a round, and the bookkeeping of a muted
// round, shared by both lesson shells.
import type { Recipe } from "@aybuben/core/content/types.ts";
import { exerciseType } from "@aybuben/core/exercises/registry.ts";
import type { LessonStep } from "@aybuben/core/lesson.ts";
import type { PracticeMode } from "@aybuben/core/practice.ts";
import type { LessonResult } from "@aybuben/core/progress/event.ts";
import { canMute, teaches } from "@aybuben/core/silent.ts";

import type { EventProps } from "../analytics/events.ts";
import { abandonProps, type LessonTag } from "../analytics/lesson.ts";
import { LISTENING_OFF_MS } from "../device/listening.ts";
import type { LessonDone, ListeningPause } from "./runner.ts";

export type { ListeningPause };

/** Why the round shows the gate: a lesson that teaches by sound, Practice's Listen mode, or nothing left to grade. */
export type GateReason = "letters" | "listen" | "empty";

interface Round {
	recipe?: Recipe;
	mode?: PracticeMode;
}

/**
 * The gate to show instead of the round, or null to open it. `mutedSteps` is
 * the round built muted: a round that grades nothing then is not worth starting.
 */
export function gateFor(round: Round & { mutedSteps: LessonStep[] }): GateReason | null {
	const { recipe, mode, mutedSteps } = round;
	if (recipe && !canMute(recipe)) return "letters";
	// Read as text, a listening round is a different round under the same name.
	if (mode === "listen") return "listen";
	if (!mutedSteps.some((s) => exerciseType(s.kind).graded)) return "empty";
	return null;
}

/**
 * Whether "Can't listen now" mutes the round in place. A lesson that teaches by
 * sound and Practice's Listen mode close instead (`closesRound`).
 */
export const canMuteRound = ({ recipe, mode }: Round): boolean =>
	recipe ? canMute(recipe) : mode !== "listen";

/** Whether "Can't listen now" closes the round onto the gate rather than muting it. */
export const closesRound = ({ recipe, mode }: Round): boolean =>
	recipe ? teaches(recipe) : mode === "listen";

export type Muted = NonNullable<LessonResult["muted"]>;

/** What building a round muted changed: steps swapped for stand-ins, and steps dropped. */
export function mutedSummary(before: LessonStep[], after: LessonStep[]): Muted {
	const had = new Set(before.map((x) => x.uid));
	const swapped = after.filter((x) => !had.has(x.uid)).length;
	// A stand-in replaces one step and a drop removes one, so the length alone is the drops.
	return { swapped, dropped: before.length - after.length, skipped: 0 };
}

/** `listening_gate_shown` before the choice is made. */
export function gateEvent(
	tag: LessonTag,
	reason: GateReason,
	minutesLeft: number,
): Omit<EventProps["listening_gate_shown"], "choice"> {
	return {
		...(tag.unitId === null ? {} : { unit_id: tag.unitId }),
		...(tag.lessonId === null ? {} : { lesson_id: tag.lessonId }),
		...(tag.mode === null ? {} : { practice_mode: tag.mode }),
		reason,
		minutes_left: minutesLeft,
	};
}

/**
 * A finished round as the shell reports it: a round built muted says so even
 * when the learner never tapped, and one silenced down to nothing graded is
 * abandoned, not recorded (there is no lesson to celebrate).
 */
export function settleDone(
	done: LessonDone,
	started: Muted | undefined,
): { done: LessonDone; empty: boolean } {
	const merged = !done.muted && started ? { ...done, muted: started } : done;
	return { done: merged, empty: merged.muted !== undefined && merged.graded === 0 };
}

/** `listening_paused`'s properties. `closed`: the tap closed a lesson that teaches by sound. */
export function pausedProps(
	tag: LessonTag,
	p: ListeningPause,
	closed = false,
): EventProps["listening_paused"] {
	return {
		...abandonProps(tag),
		lesson_closed: closed,
		exercise: p.exercise,
		step_index: p.stepIndex,
		steps_swapped: p.swapped,
		steps_dropped: p.dropped,
		off_minutes: LISTENING_OFF_MS / 60_000,
	};
}

/** What an abandonment reports: the round's muted build, else what a tap changed. */
export const abandonedMuted = (started: Muted | undefined, tapped: Muted | undefined) =>
	started ?? tapped;
